/**
 * Determinism probe: a seeded scripted match run headless, returning `hashState` at checkpoints. The same
 * function runs in Node (Vitest) and in the browser (`window.__HF__.simProbe`, e2e/determinism.spec.ts), so the
 * two runtimes are compared bit-for-bit on every e2e run (ARCHITECTURE §4, §10). No golden constants to maintain:
 * the e2e spec computes the Node side at test time.
 */
import { INPUT_MASK, Prng, defaultMatchConfig, type InputFrame, type MatchConfig } from '@hf/shared';
import { hashState } from './hash';
import { createInitialState } from './state';
import { step } from './step';

export const PROBE_CONFIG: MatchConfig = defaultMatchConfig({
  seed: 0x5eed1e55,
  characters: ['zorg', 'bigtex'],
  wind: 1.25,
});

/** Default checkpoints (ticks) for the probe. */
export const PROBE_CHECKPOINTS: readonly number[] = [1_000, 5_000, 10_000];

/**
 * Run `max(checkpoints)` ticks of seeded, "human-like" inputs (each player holds a random frame for 1–12 ticks)
 * and return hashState after each checkpoint tick. Events are not collected (headless).
 */
export const runDeterminismProbe = (
  checkpoints: readonly number[] = PROBE_CHECKPOINTS,
  config: MatchConfig = PROBE_CONFIG,
  inputSeed = 7,
): number[] => {
  const s = createInitialState(config);
  const prng = new Prng(inputSeed);
  const inputs: InputFrame[] = [0, 0];
  const hold = [0, 0];
  const last = checkpoints.reduce((a, b) => (b > a ? b : a), 0);
  const out: number[] = [];
  let next = 0;
  for (let t = 1; t <= last; t++) {
    for (let p = 0; p < 2; p++) {
      if (hold[p]! <= 0) {
        inputs[p] = prng.nextU32() & INPUT_MASK;
        hold[p] = 1 + prng.int(12);
      }
      hold[p] = hold[p]! - 1;
    }
    step(s, inputs, null);
    while (next < checkpoints.length && checkpoints[next] === t) {
      out.push(hashState(s));
      next++;
    }
  }
  return out;
};
